import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Room } from '@/types/room';
import { hexToNum } from '@/utils/colors';

const FLOOR_COLOR = 0x0a0e1a;
const WALL_COLOR = 0x1e2a3a;
const CORRIDOR_COLOR = 0x080c14;
const COMMON_ROOM_COLOR = 0x12183a;

/** Dessine le plan du QG (statique — recalculé uniquement si les salles changent) */
export class FloorMapRenderer {
  private container: Container;
  private app: Application;

  constructor(app: Application) {
    this.app = app;
    this.container = new Container();
    this.app.stage.addChildAt(this.container, 0);
  }

  render(rooms: Room[]) {
    this.container.removeChildren();

    // ── Fond général ────────────────────────────────────────────────────
    const bg = new Graphics();
    bg.rect(0, 0, this.app.canvas.width, this.app.canvas.height).fill(FLOOR_COLOR);
    this.container.addChild(bg);

    // ── Couloirs (zones entre rangées) ──────────────────────────────────
    const corridors = new Graphics();
    // Corridor horizontal entre row1 et row2 (y=130-150)
    corridors.rect(0, 130, this.app.canvas.width, 20).fill(CORRIDOR_COLOR);
    // Corridor horizontal entre row2 et row3 (y=270-290)
    corridors.rect(0, 270, this.app.canvas.width, 20).fill(CORRIDOR_COLOR);
    this.container.addChild(corridors);

    // Petits marqueurs de couloir (points lumineux)
    const hallDots = new Graphics();
    for (let x = 20; x < this.app.canvas.width; x += 40) {
      hallDots.circle(x, 140, 2).fill({ color: 0x1e3a5a, alpha: 0.8 });
      hallDots.circle(x, 280, 2).fill({ color: 0x1e3a5a, alpha: 0.8 });
    }
    this.container.addChild(hallDots);

    // ── Salles ──────────────────────────────────────────────────────────
    for (const room of rooms) {
      this.drawRoom(room);
    }

    // ── Scanline grid overlay (légère) ──────────────────────────────────
    const grid = new Graphics();
    grid.rect(0, 0, this.app.canvas.width, this.app.canvas.height)
      .fill({ color: 0x000000, alpha: 0 }); // transparent, sert de conteneur
    this.container.addChild(grid);
  }

  private drawRoom(room: Room) {
    const { pixelX: x, pixelY: y, pixelW: w, pixelH: h, color, type, label } = room;
    const accentNum = hexToNum(color);

    // Fond de la salle
    const fill = new Graphics();
    const fillColor = type === 'store' ? 0x0e0a1a : type === 'common' ? COMMON_ROOM_COLOR : 0x0d1117;
    fill.rect(x + 1, y + 1, w - 2, h - 2).fill({ color: fillColor });
    this.container.addChild(fill);

    // Bordure néon (glow simulé par plusieurs rectangles)
    const border = new Graphics();
    border.rect(x, y, w, h).stroke({ width: 1, color: 0x1e2a3a });
    border.rect(x + 1, y + 1, w - 2, h - 2).stroke({ width: 1, color: accentNum, alpha: 0.4 });
    this.container.addChild(border);

    // Coin supérieur gauche — accent de couleur
    const cornerTL = new Graphics();
    cornerTL.rect(x, y, 8, 2).fill({ color: accentNum, alpha: 0.9 });
    cornerTL.rect(x, y, 2, 8).fill({ color: accentNum, alpha: 0.9 });
    this.container.addChild(cornerTL);

    // Coin inférieur droit
    const cornerBR = new Graphics();
    cornerBR.rect(x + w - 8, y + h - 2, 8, 2).fill({ color: accentNum, alpha: 0.9 });
    cornerBR.rect(x + w - 2, y + h - 8, 2, 8).fill({ color: accentNum, alpha: 0.9 });
    this.container.addChild(cornerBR);

    // Sol en damier subtil
    const tiles = new Graphics();
    const tileSize = 16;
    for (let tx = x + 2; tx < x + w - 2; tx += tileSize) {
      for (let ty = y + 2; ty < y + h - 2; ty += tileSize) {
        const isAlt = ((Math.floor((tx - x) / tileSize) + Math.floor((ty - y) / tileSize)) % 2 === 0);
        if (isAlt) {
          tiles.rect(tx, ty, Math.min(tileSize, x + w - 2 - tx), Math.min(tileSize, y + h - 2 - ty))
            .fill({ color: fillColor + 0x040408, alpha: 0.5 });
        }
      }
    }
    this.container.addChild(tiles);

    // Mobilier symbolique selon le type
    this.drawFurniture(room);

    // Label de salle
    const textStyle = new TextStyle({
      fontFamily: '"Press Start 2P", monospace',
      fontSize: 6,
      fill: accentNum,
    });
    const labelText = new Text({ text: label, style: textStyle });
    labelText.alpha = 0.9;
    labelText.x = x + 6;
    labelText.y = y + 6;
    this.container.addChild(labelText);

    // Mur du haut (accent coloré) pour les boutiques
    if (type === 'store') {
      const storeBar = new Graphics();
      storeBar.rect(x + 2, y, w - 4, 3).fill({ color: accentNum, alpha: 0.7 });
      this.container.addChild(storeBar);
    }
  }

  private drawFurniture(room: Room) {
    const { pixelX: x, pixelY: y, pixelW: w, pixelH: h, type, color } = room;
    const accentNum = hexToNum(color);
    const furniture = new Graphics();

    if (type === 'department') {
      // Bureau (rectangle simple)
      const deskX = x + Math.round(w * 0.15);
      const deskY = y + Math.round(h * 0.3);
      const deskW = Math.round(w * 0.3);
      furniture.rect(deskX, deskY, deskW, 10).fill({ color: WALL_COLOR });
      furniture.rect(deskX, deskY, deskW, 10).stroke({ width: 1, color: accentNum, alpha: 0.3 });

      const desk2X = x + Math.round(w * 0.55);
      furniture.rect(desk2X, deskY, deskW, 10).fill({ color: WALL_COLOR });
      furniture.rect(desk2X, deskY, deskW, 10).stroke({ width: 1, color: accentNum, alpha: 0.3 });

      // Écran (minuscule rectangle lumineux)
      furniture.rect(deskX + 4, deskY - 6, 14, 5).fill({ color: accentNum, alpha: 0.2 });
      furniture.rect(desk2X + 4, deskY - 6, 14, 5).fill({ color: accentNum, alpha: 0.2 });

    } else if (type === 'store') {
      // Étal / vitrine
      const shelfY = y + Math.round(h * 0.55);
      furniture.rect(x + 10, shelfY, w - 20, 6).fill({ color: WALL_COLOR });
      furniture.rect(x + 10, shelfY, w - 20, 6).stroke({ width: 1, color: accentNum, alpha: 0.4 });
      // Petits produits sur l'étal
      for (let px = x + 16; px < x + w - 16; px += 12) {
        furniture.rect(px, shelfY - 6, 8, 6).fill({ color: accentNum, alpha: 0.15 });
      }

    } else if (type === 'common') {
      // Table de réunion (ovale simulé par rectangle arrondi)
      const tableX = x + Math.round(w * 0.2);
      const tableY = y + Math.round(h * 0.3);
      const tableW = Math.round(w * 0.6);
      const tableH = Math.round(h * 0.35);
      furniture.rect(tableX, tableY, tableW, tableH).fill({ color: WALL_COLOR });
      furniture.rect(tableX, tableY, tableW, tableH).stroke({ width: 1, color: accentNum, alpha: 0.5 });
      // Chaises
      for (let cx = tableX + 8; cx < tableX + tableW - 4; cx += 14) {
        furniture.rect(cx, tableY - 5, 8, 4).fill({ color: WALL_COLOR });
        furniture.rect(cx, tableY + tableH + 1, 8, 4).fill({ color: WALL_COLOR });
      }
    }

    this.container.addChild(furniture);
  }

  destroy() {
    this.container.destroy({ children: true });
  }
}
